export const GOOGLE_LEAD_QUEUE_STORAGE_KEY = "wd_google_lead_queue_v1";

const CONSENT_STORAGE_KEY = "waterdoctor_consent";
const CONSENT_STORAGE_VERSION = 1;
const CONSENT_CHANGE_EVENT = "waterdoctor:consent-change";

const DEFAULT_CONSENT = Object.freeze({
  version: CONSENT_STORAGE_VERSION,
  hasChoice: false,
  analytics: false,
  advertising: false,
  decidedAt: null,
});

let inMemoryConsent = null;

function normalizeStoredConsent(value) {
  if (!value || typeof value !== "object") return null;
  if (value.version !== CONSENT_STORAGE_VERSION) return null;
  if (typeof value.analytics !== "boolean") return null;
  if (typeof value.advertising !== "boolean") return null;

  return {
    version: CONSENT_STORAGE_VERSION,
    hasChoice: true,
    analytics: value.analytics,
    advertising: value.advertising,
    decidedAt: typeof value.decidedAt === "string" ? value.decidedAt : null,
  };
}

function readConsentSnapshot() {
  if (typeof window === "undefined") return { ...DEFAULT_CONSENT };

  try {
    const storedValue = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    const parsedValue = storedValue ? JSON.parse(storedValue) : null;
    const storedConsent = normalizeStoredConsent(parsedValue);
    if (storedConsent) {
      inMemoryConsent = storedConsent;
      return { ...storedConsent };
    }
  } catch {
    // Fall back to the current-tab choice when storage is blocked.
  }

  return inMemoryConsent ? { ...inMemoryConsent } : { ...DEFAULT_CONSENT };
}

function toGoogleConsentState(snapshot) {
  const analyticsGranted = snapshot?.analytics === true;
  const advertisingGranted = snapshot?.advertising === true;

  return {
    analytics_storage: analyticsGranted ? "granted" : "denied",
    ad_storage: advertisingGranted ? "granted" : "denied",
    ad_user_data: advertisingGranted ? "granted" : "denied",
    ad_personalization: advertisingGranted ? "granted" : "denied",
  };
}

function applyConsentEffects(snapshot) {
  if (typeof window === "undefined") return;
  if (!snapshot.analytics) {
    try { window.sessionStorage?.removeItem(GOOGLE_LEAD_QUEUE_STORAGE_KEY); } catch { /* Storage may be restricted. */ }
  }
  window.gtag?.("consent", "update", toGoogleConsentState(snapshot));
  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGE_EVENT, { detail: { ...snapshot } }),
  );
}

function applyExternalConsentStorageEvent(event) {
  if (event.key !== CONSENT_STORAGE_KEY && event.key !== null) return false;
  let storedConsent = null;
  if (event.key === CONSENT_STORAGE_KEY && event.newValue) {
    try {
      storedConsent = normalizeStoredConsent(JSON.parse(event.newValue));
    } catch {
      // Invalid or removed consent records fall back to denied defaults.
    }
  }
  inMemoryConsent = storedConsent;
  applyConsentEffects(storedConsent || DEFAULT_CONSENT);
  return true;
}

function subscribeConsentChanges(onChange) {
  const onStorage = (event) => applyExternalConsentStorageEvent(event);
  window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
  };
}

function saveConsentPreferences({ analytics, advertising }) {
  const snapshot = {
    version: CONSENT_STORAGE_VERSION,
    hasChoice: true,
    analytics: analytics === true,
    advertising: advertising === true,
    decidedAt: new Date().toISOString(),
  };

  inMemoryConsent = snapshot;

  if (typeof window === "undefined") return snapshot;

  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({
        version: snapshot.version,
        analytics: snapshot.analytics,
        advertising: snapshot.advertising,
        decidedAt: snapshot.decidedAt,
      }),
    );
  } catch {
    // The in-memory choice still applies when storage is unavailable.
  }

  applyConsentEffects(snapshot);

  return snapshot;
}

export {
  CONSENT_CHANGE_EVENT,
  CONSENT_STORAGE_KEY,
  CONSENT_STORAGE_VERSION,
  DEFAULT_CONSENT,
  applyExternalConsentStorageEvent,
  normalizeStoredConsent,
  readConsentSnapshot,
  saveConsentPreferences,
  subscribeConsentChanges,
  toGoogleConsentState,
};
